'use client';

import { useState, useRef, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { EmailIcon } from '@/components/icons/EmailIcon';
import { Button } from '@/components/atoms';
import { useDashboard } from '@/modules/dashboard/context/DashboardContext';

interface Contact {
  name: string;
  email: string;
  role: string;
}

// Helper function to format name from "Last, First" to "First Last"
const formatName = (name: string): string => {
  if (name.includes(',')) {
    const [lastName, firstName] = name.split(',').map(part => part.trim());
    return `${firstName} ${lastName}`;
  }
  return name;
};

export const ContactSalesDropdown = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const dropdownRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const { state } = useDashboard();
  const { selectedDealer } = state;

  // Build contacts list from selected dealer
  const contacts = useMemo(() => {
    const contactList: Contact[] = [];
    
    if (selectedDealer?.accountmanagerName && selectedDealer?.accountmanagerEmail) {
      // Skip if the name is "Account, House" (case-insensitive)
      if (selectedDealer.accountmanagerName.toLowerCase() !== 'account, house') {
        contactList.push({
          name: selectedDealer.accountmanagerName,
          email: selectedDealer.accountmanagerEmail,
          role: 'Account Manager',
        });
      }
    }
    
    if (selectedDealer?.salespersonName && selectedDealer?.salespersonEmail) {
      // Skip if the name is "Account, House" (case-insensitive)
      if (selectedDealer.salespersonName.toLowerCase() !== 'account, house') {
        contactList.push({
          name: selectedDealer.salespersonName,
          email: selectedDealer.salespersonEmail,
          role: 'Salesperson',
        });
      }
    }
    
    return contactList;
  }, [selectedDealer]);

  useEffect(() => {
    if (isOpen && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setPosition({
        top: rect.bottom + 8,
        left: rect.right - 320, // 320px width (w-80) aligned to right
      });
    }
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (triggerRef.current && !triggerRef.current.contains(event.target as Node)) {
        const dropdownContent = document.getElementById('contact-sales-dropdown');
        if (dropdownContent && !dropdownContent.contains(event.target as Node)) {
          setIsOpen(false);
        }
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  const handleToggle = () => setIsOpen(!isOpen);

  const dropdownContent = isOpen ? (
    <div
      id="contact-sales-dropdown"
      className="fixed bg-white rounded-lg shadow-lg p-4 w-80 z-[9999] animate-fadeIn border border-gray-200"
      style={{
        top: position.top,
        left: position.left,
      }}
    >
      <h3 className="text-lg font-bold text-gray-900 mb-4">Sales Contact</h3>

      {contacts.length > 0 ? (
        <div className="space-y-4">
          {contacts.map((contact) => (
            <div key={contact.email} className="flex flex-col gap-1">
              <p className="text-sm font-semibold text-gray-900">{formatName(contact.name)}</p>
              <a
                href={`mailto:${contact.email}`}
                className="text-sm text-[#449995] hover:underline transition-colors"
              >
                {contact.email}
              </a>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-sm text-gray-500">No contact information available. Please select a dealer.</p>
      )}
    </div>
  ) : null;

  return (
    <>
      <div className="relative" ref={triggerRef}>
        <Button
          variant='ghost'
          className='text-white'
          onClick={handleToggle}
        >
          <div className="flex items-center gap-2">
            <div className="w-[24px] h-[24px]">
              <EmailIcon />
            </div>
            <span style={{ fontFamily: 'Roboto, sans-serif' }}>Contact Sales</span>
          </div>
        </Button>
      </div>
      {typeof window !== 'undefined' && dropdownContent && createPortal(dropdownContent, document.body)}
    </>
  );
};
